import type { CompanyContact } from '@tandemone/types'
import { useQuery } from '@tanstack/react-query'

import { getSupplierBusinessContacts } from './api'
import { BusinessContactsFormProvider } from './business-contacts-provider'
import { type FieldDef, ContactRowList } from './contact-row-list'
import {
  SUPPLIER_CONTACT_ROLE_LABELS,
  SUPPLIER_CONTACT_ROLES
} from './enum-labels'
import { FormSkeleton } from './form-skeleton'
import { PrimaryButton } from './primary-button'
import { StepFooter } from './step-footer'
import { useBusinessContactsForm } from './use-business-contacts-form'

const ROLE_OPTIONS = SUPPLIER_CONTACT_ROLES.map(value => ({
  value,
  label: SUPPLIER_CONTACT_ROLE_LABELS[value]
}))

const FIELDS: ReadonlyArray<FieldDef<CompanyContact>> = [
  { key: 'name', label: 'Name', required: true },
  {
    key: 'role',
    label: 'Role',
    type: 'select',
    options: ROLE_OPTIONS,
    placeholder: 'Pick a role'
  },
  { key: 'email', label: 'Email', type: 'email', required: true },
  { key: 'phone', label: 'Phone', type: 'tel' }
]

const EMPTY_DRAFT: Omit<CompanyContact, 'id'> = {
  name: '',
  role: null,
  phone: null,
  email: ''
}

/** company-level contacts, shared by the supplier and buyer flows. */
export function CompanyContactsForm({
  onSuccess,
  submitLabel = 'Save and continue'
}: {
  onSuccess: () => void
  submitLabel?: string
}) {
  return (
    <BusinessContactsFormProvider onSuccess={onSuccess}>
      <FormBody submitLabel={submitLabel} />
    </BusinessContactsFormProvider>
  )
}

function FormBody({ submitLabel }: { submitLabel: string }) {
  const { handleContinue, addRow, updateRow, removeRow } =
    useBusinessContactsForm()
  const { data, isLoading } = useQuery({
    queryKey: ['supplier-business-contacts'],
    queryFn: ({ signal }) => getSupplierBusinessContacts(signal)
  })

  if (isLoading) return <FormSkeleton />
  const rows = data?.contacts ?? []
  const canContinue = rows.length >= 1

  return (
    <div className='flex w-full flex-col gap-4'>
      <section className='rounded-2xl border border-border bg-page p-6'>
        <ContactRowList<CompanyContact>
          rows={rows}
          fields={FIELDS}
          emptyDraft={EMPTY_DRAFT}
          addLabel='Add another contact'
          emptyState='Add at least one business contact'
          onAdd={addRow}
          onSave={updateRow}
          onRemove={removeRow}
        />
      </section>

      <StepFooter
        hint={
          rows.length === 0
            ? 'Add at least one contact'
            : `${rows.length} contact${rows.length === 1 ? '' : 's'}`
        }
      >
        <PrimaryButton
          type='button'
          disabled={!canContinue}
          onClick={handleContinue}
        >
          {submitLabel}
        </PrimaryButton>
      </StepFooter>
    </div>
  )
}
